import axios, { AxiosError, AxiosResponse } from "axios";
import { call, put, takeLatest } from "@redux-saga/core/effects";

import { RootState } from "data-handler/rootReducer";
import { Category } from "SCConstants";

// dedicated slice, deliberately not reusing storeDoctor's `data` — that key is
// persisted (redux-persist) and drives the CountryAdminStocksTable list, so
// fetching a school's full stock list here would clobber the table's state
export const STOCK_SIBLINGS_REQUEST =
  "schoolconnect/stockSiblings/STOCK_SIBLINGS_REQUEST";
export const STOCK_SIBLINGS_SUCCESS =
  "schoolconnect/stockSiblings/STOCK_SIBLINGS_SUCCESS";
export const STOCK_SIBLINGS_FAILURE =
  "schoolconnect/stockSiblings/STOCK_SIBLINGS_FAILURE";

export type StockSibling = {
  stock_id: number;
  commodity_id: number;
  commodity_name: string;
  category: Category;
  // older backends don't send this yet - treat missing as "has movements"
  has_events?: boolean;
};

type State = {
  fetching: boolean;
  error: AxiosError | null;
  bySchoolId: Record<number, StockSibling[]>;
};

const initialState: State = {
  fetching: false,
  error: null,
  bySchoolId: {},
};

type StockSiblingsRequestAction = {
  type: typeof STOCK_SIBLINGS_REQUEST;
  schoolId: number;
};

type StockSiblingsSuccessAction = {
  type: typeof STOCK_SIBLINGS_SUCCESS;
  schoolId: number;
  siblings: StockSibling[];
};

type StockSiblingsFailureAction = {
  type: typeof STOCK_SIBLINGS_FAILURE;
  schoolId: number;
  error: AxiosError;
};

type Action =
  | StockSiblingsRequestAction
  | StockSiblingsSuccessAction
  | StockSiblingsFailureAction;

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case STOCK_SIBLINGS_REQUEST:
      return { ...state, fetching: true, error: null };
    case STOCK_SIBLINGS_SUCCESS:
      return {
        ...state,
        fetching: false,
        error: null,
        bySchoolId: {
          ...state.bySchoolId,
          [action.schoolId]: action.siblings,
        },
      };
    case STOCK_SIBLINGS_FAILURE:
      // mark the school as settled (empty) even on failure — otherwise a
      // school that keeps failing (or genuinely has zero rows) never looks
      // "fetched" and the component effect below retries on every render
      return {
        ...state,
        fetching: false,
        error: action.error,
        bySchoolId: {
          ...state.bySchoolId,
          [action.schoolId]: state.bySchoolId[action.schoolId] ?? [],
        },
      };
    default:
      return state;
  }
};

export default reducer;

// the store-doctor list endpoint caps at 100 rows/page server-side no matter
// what limit we ask for, so a school with more stocks than that needs a few
// extra pages — MAX_PAGES is just a runaway guard, not an expected ceiling
const PAGE_LIMIT = 200;
const MAX_PAGES = 10;

function fetchStockSiblingsPage(schoolId: number, offset: number) {
  return axios({
    method: "GET",
    url:
      `${process.env.REACT_APP_API_URL}/store_manager/store-doctor/` +
      `?limit=${PAGE_LIMIT}&offset=${offset}&school_id=${schoolId}` +
      // deactivated schools still hold stock, and this list is reached from a stock we are
      // already inspecting - filtering the school out returns an empty list that reads as
      // "no stock here" rather than "hidden". school 12478 has 112 stocks and showed none.
      `&include_inactive=true`,
  });
}

export function* workerStockSiblingsSaga(action: StockSiblingsRequestAction) {
  const { schoolId } = action;
  try {
    let siblings: StockSibling[] = [];
    let offset = 0;
    let total = Infinity;
    let pages = 0;

    while (siblings.length < total && pages < MAX_PAGES) {
      const response: AxiosResponse = yield call(
        fetchStockSiblingsPage,
        schoolId,
        offset
      );
      total = response.data.count;
      siblings = siblings.concat(response.data.results);
      pages += 1;
      if (!response.data.results.length) break;
      offset += response.data.results.length;
    }

    yield put({ type: STOCK_SIBLINGS_SUCCESS, schoolId, siblings });
  } catch (error) {
    yield put({ type: STOCK_SIBLINGS_FAILURE, schoolId, error });
  }
}

export function* stockSiblingsSagaWatcher() {
  yield takeLatest(STOCK_SIBLINGS_REQUEST, workerStockSiblingsSaga);
}

export const requestStockSiblings = (
  schoolId: number
): StockSiblingsRequestAction => ({
  type: STOCK_SIBLINGS_REQUEST,
  schoolId,
});

export const getStockSiblings = (
  state: RootState,
  schoolId: number
): StockSibling[] => state.stockSiblings.bySchoolId[schoolId] ?? [];

// distinguishes "never asked" from "asked, got zero rows" — an empty array
// alone can't tell those apart, and conflating them causes an infinite
// refetch loop for a school that legitimately (or currently) has no rows
export const hasFetchedStockSiblings = (
  state: RootState,
  schoolId: number
): boolean =>
  Object.prototype.hasOwnProperty.call(
    state.stockSiblings.bySchoolId,
    schoolId
  );

export const getIsStockSiblingsFetching = (state: RootState): boolean =>
  state.stockSiblings.fetching;
